import React, { useState, useEffect } from 'react';
import {
  FaCertificate, FaAward, FaGlobe, FaCheckCircle, FaStar,
  FaDownload, FaArrowRight, FaUserGraduate, FaChartLine,
  FaShieldAlt, FaBook, FaCalendarAlt, FaClock, FaUsers,
  FaLaptopCode, FaFileAlt, FaSearch, FaFilter, FaInfoCircle,
  FaGraduationCap, FaTrophy, FaRocket, FaLightbulb, FaBriefcase,
  FaHandshake, FaChalkboardTeacher, FaUniversity, FaMedal, FaUserPlus,
  FaSpinner, FaExclamationTriangle
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link, useNavigate } from 'react-router-dom';

// ── URL de base de l'API ──────────────────────────────────────────────────────
import { API_URL, resolveImageUrl } from '../config/api';

const CertificationsCINEF = () => {
  const navigate = useNavigate();
  const [certifications,   setCertifications]   = useState([]);
  const [loading,          setLoading]           = useState(true);
  const [error,            setError]             = useState(null);
  const [searchTerm,       setSearchTerm]        = useState('');
  const [selectedDomain,   setSelectedDomain]    = useState('all');
  const [selectedDuration, setSelectedDuration]  = useState('all');
  const [visibleSections,  setVisibleSections]   = useState(new Set(['certifications', 'advantages', 'process']));

  // ── Intersection Observer ──────────────────────────────────────────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) setVisibleSections(prev => new Set([...prev, e.target.dataset.section]));
      }),
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );
    document.querySelectorAll('[data-section]').forEach(s => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // ── Fetch depuis l'API (certifications actives uniquement) ─────────────────
  useEffect(() => {
    setLoading(true);
    setError(null);

    fetch(`${API_URL}/formations/certifications/public`)
      .then(res => {
        if (!res.ok) throw new Error(`Erreur ${res.status}`);
        return res.json();
      })
      .then(data => {
        setCertifications(data.data || data || []);
      })
      .catch(err => {
        console.error('Erreur fetch certifications:', err);
        setError('Impossible de charger les certifications. Veuillez réessayer.');
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Inscription ────────────────────────────────────────────────────────────
  const handleInscription = (certification) => {
    navigate('/inscriptionformation', {
      state: {
        certification: {
          id:       certification.id,
          code:     certification.code,
          title:    certification.title,
          domain:   certification.domain,
          price:    certification.price,
          duration: certification.duration,
          image:    certification.image,
          type:     'certification',
        }
      }
    });
  };

  // ── Filtres dynamiques ─────────────────────────────────────────────────────
  const domains   = [...new Set(certifications.map(c => c.domain).filter(Boolean))];
  const durations = [...new Set(certifications.map(c => c.duration).filter(Boolean))];

  const filteredCertifications = certifications.filter(cert => {
    const matchSearch   = !searchTerm || cert.title?.toLowerCase().includes(searchTerm.toLowerCase()) || cert.domain?.toLowerCase().includes(searchTerm.toLowerCase()) || cert.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchDomain   = selectedDomain   === 'all' || cert.domain   === selectedDomain;
    const matchDuration = selectedDuration === 'all' || cert.duration === selectedDuration;
    return matchSearch && matchDomain && matchDuration;
  });

  // ── Données statiques ──────────────────────────────────────────────────────
  const stats = [
    { icon: FaCertificate,  value: loading ? '…' : `${certifications.length}+`, label: 'Certifications CINEF', color: '#198754' },
    { icon: FaUsers,        value: '2000+',  label: 'Certifiés',          color: '#20c997' },
    { icon: FaBriefcase,    value: '85%',    label: "Taux d'insertion",   color: '#198754' },
    { icon: FaTrophy,       value: '95%',    label: 'Satisfaction',       color: '#20c997' },
  ];

  const advantages = [
    { icon: FaMedal,           title: 'Certification Reconnue',       description: "Diplômes et certificats CINEF reconnus par l'État et les entreprises en Afrique de l'Ouest", color: '#198754' },
    { icon: FaChalkboardTeacher, title: 'Formateurs Qualifiés',       description: 'Enseignants et professionnels expérimentés avec une pédagogie pratique et efficace',           color: '#20c997' },
    { icon: FaHandshake,       title: 'Accompagnement Personnalisé',  description: 'Suivi individuel, coaching et aide à l\'insertion professionnelle tout au long du parcours',    color: '#198754' },
    { icon: FaLaptopCode,      title: 'Formation Pratique',           description: "Projets réels, études de cas et mise en situation pour une employabilité immédiate",            color: '#20c997' },
  ];

  const processSteps = [
    { number: '01', title: 'Orientation',  description: 'Rencontrez nos conseillers pour identifier la certification adaptée à vos objectifs',        icon: FaInfoCircle },
    { number: '02', title: 'Inscription',  description: "Complétez votre dossier d'inscription et validez votre parcours de formation",               icon: FaFileAlt    },
    { number: '03', title: 'Formation',    description: 'Suivez votre formation avec nos experts et réalisez vos projets pratiques',                   icon: FaBook       },
    { number: '04', title: 'Évaluation',   description: 'Passez les évaluations continues et l\'examen final de certification',                       icon: FaChartLine  },
    { number: '05', title: 'Certification',description: 'Recevez votre certificat CINEF et bénéficiez de notre accompagnement carrière',               icon: FaAward      },
  ];

  return (
    <>
      <Header />
      <div className="cinef-certifications-wrapper">
        {/* Hero */}
        <section className="cinef-hero">
          <div className="cinef-container">
            <div className="hero-content">
              <div className="hero-badge">
                <FaCertificate className="badge-icon" />
                <span>Certifications Professionnelles CINEF</span>
              </div>
              {/* <h1 className="hero-title">
                Développez vos compétences<br />
                avec nos <span className="gradient-text">certifications</span>
              </h1> */}
            </div>
          </div>
        </section>

        {/* Certifications List */}
        <section id="certifications-list" className="certifications-list-section visible" style={{marginTop: '-200px'}} data-section="certifications">
          <div className="cinef-container">
            <div className="section-header centered" style={{marginTop: '-200px'}}>
              <span className="section-badge">
                <span className="badge-dot"></span>
                Nos formations certifiantes
              </span>
              <h2 className="section-title">
                Trouvez votre <span className="highlight-text">Certification CINEF</span>
              </h2>
            </div>

            {/* Filters */}
            <div className="filters-section">
              <div className="search-box">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Rechercher une certification..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="filters-group">
                <div className="filter-item">
                  <FaBook className="filter-icon" />
                  <select value={selectedDomain} onChange={(e) => setSelectedDomain(e.target.value)} className="filter-select">
                    <option value="all">Tous les domaines</option>
                    {domains.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="filter-item">
                  <FaClock className="filter-icon" />
                  <select value={selectedDuration} onChange={(e) => setSelectedDuration(e.target.value)} className="filter-select">
                    <option value="all">Toutes les durées</option>
                    {durations.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div className="results-count">
                <FaCheckCircle />
                <span>
                  {loading
                    ? 'Chargement…'
                    : `${filteredCertifications.length} certification${filteredCertifications.length > 1 ? 's' : ''} disponible${filteredCertifications.length > 1 ? 's' : ''}`
                  }
                </span>
              </div>
            </div>

            {/* État de chargement */}
            {loading && (
              <div className="loading-state">
                <FaSpinner className="spin-icon" />
                <p>Chargement des certifications…</p>
              </div>
            )}

            {/* État d'erreur */}
            {!loading && error && (
              <div className="error-state">
                <FaExclamationTriangle className="error-icon" />
                <h3>Une erreur est survenue</h3>
                <p>{error}</p>
                <button onClick={() => window.location.reload()} className="btn-reset">Réessayer</button>
              </div>
            )}

            {/* Grid */}
            {!loading && !error && (
              filteredCertifications.length > 0 ? (
                <div className="certifications-grid">
                  {filteredCertifications.map((cert, index) => (
                    <div key={cert.id} className="certification-card" style={{ '--cert-color': cert.color || '#198754', animationDelay: `${index * 0.08}s` }}>
                      <div className="cert-header">
                        <div className="cert-image-wrapper">
                          {cert.image ? (
                            <img src={resolveImageUrl(cert.image)} alt={cert.title} className="cert-image" />
                          ) : (
                            <div className="cert-image-placeholder">
                              <FaCertificate />
                            </div>
                          )}
                          <div className="cert-overlay"></div>
                        </div>
                        <div className="cert-badge"><FaCertificate /><span>CINEF</span></div>
                        <div className="cert-code-badge">{cert.code}</div>
                      </div>

                      <div className="cert-content">
                        <div className="cert-meta">
                          <span className="cert-domain" style={{ color: cert.color || '#198754' }}>
                            <FaBook />{cert.domain}
                          </span>
                          <span className="cert-duration">
                            <FaClock />{cert.duration}
                          </span>
                        </div>
                        <h3 className="cert-title">{cert.title}</h3>
                        <p className="cert-description">{cert.description}</p>

                        <div className="cert-details">
                          {cert.modules && cert.modules.length > 0 && (
                            <div className="detail-section">
                              <h4><FaBook /> Modules clés</h4>
                              <ul>
                                {cert.modules.slice(0, 3).map((module, idx) => (
                                  <li key={idx}><FaCheckCircle /><span>{module}</span></li>
                                ))}
                                {cert.modules.length > 3 && (
                                  <li className="more-items">+ {cert.modules.length - 3} autres modules</li>
                                )}
                              </ul>
                            </div>
                          )}
                          {cert.debouches && cert.debouches.length > 0 && (
                            <div className="detail-section">
                              <h4><FaBriefcase /> Débouchés</h4>
                              <div className="debouches-tags">
                                {cert.debouches.map((d, idx) => (
                                  <span key={idx} className="tag">{d}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="cert-footer">
                          <div className="cert-info-row">
                            <div className="info-item">
                              <FaLaptopCode /><span>{cert.mode}</span>
                            </div>
                          </div>
                          <div className="cert-price">
                            <span className="price-label">Prix</span>
                            <span className="price-value">{cert.price}</span>
                          </div>
                        </div>

                        <button className="btn-inscription" onClick={() => handleInscription(cert)}>
                          <FaUserPlus />
                          <span>S'inscrire à cette formation</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-results">
                  <FaSearch className="no-results-icon" />
                  <h3>Aucune certification trouvée</h3>
                  <p>Essayez de modifier vos critères de recherche</p>
                  <button onClick={() => { setSearchTerm(''); setSelectedDomain('all'); setSelectedDuration('all'); }} className="btn-reset">
                    Réinitialiser les filtres
                  </button>
                </div>
              )
            )}

            <div className="other-certifications-section">
              <div className="divider-line"></div>
              <Link to="/certifications" className="btn-other-certifications">
                <FaCertificate />
                <span>Voir toutes nos autres certifications</span>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="stats-section">
          <div className="cinef-container">
            <div className="stats-grid">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="stat-card" style={{ '--stat-color': stat.color }}>
                    <div className="stat-icon"><Icon /></div>
                    <div className="stat-content">
                      <div className="stat-value">{stat.value}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="advantages-section visible" data-section="advantages">
          <div className="cinef-container">
            <div className="section-header">
              <span className="section-badge"><span className="badge-dot"></span>Pourquoi choisir CINEF</span>
              <h2 className="section-title">Des Certifications qui <span className="highlight-text">Comptent</span></h2>
              <p className="section-description">CINEF s'engage à vous offrir des formations de qualité avec un accompagnement personnalisé</p>
            </div>
            <div className="advantages-grid">
              {advantages.map((adv, index) => {
                const Icon = adv.icon;
                return (
                  <div key={index} className="advantage-card">
                    <div className="advantage-icon" style={{ background: adv.color }}><Icon /></div>
                    <h3>{adv.title}</h3>
                    <p>{adv.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="process-section visible" data-section="process">
          <div className="cinef-container">
            <div className="section-header centered">
              <span className="section-badge"><span className="badge-dot"></span>Comment ça marche</span>
              <h2 className="section-title">Votre parcours de <span className="highlight-text">certification</span></h2>
              <p className="section-description">5 étapes simples pour obtenir votre certification CINEF</p>
            </div>
            <div className="process-timeline">
              {processSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="process-step">
                    <div className="step-number">{step.number}</div>
                    <div className="step-content">
                      <div className="step-icon"><Icon /></div>
                      <h3>{step.title}</h3>
                      <p>{step.description}</p>
                    </div>
                    {index < processSteps.length - 1 && <div className="step-connector"></div>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <style>{styles}</style>
      </div>
      <Footer />
    </>
  );
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  * { margin:0; padding:0; box-sizing:border-box }
  .cinef-certifications-wrapper { font-family:'Inter',sans-serif; background:#ffffff; overflow-x:hidden }
  .cinef-container { max-width:1280px; margin:0 auto; padding:0 24px }

  @keyframes fadeInUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.05);opacity:0.8} }
  @keyframes spin { to{transform:rotate(360deg)} }

  /* Hero */
  .cinef-hero { padding:120px 0 80px; background:linear-gradient(180deg,#f8fafc 0%,#ffffff 100%); position:relative; overflow:hidden }
  .cinef-hero::before { content:''; position:absolute; top:-200px; right:-200px; width:500px; height:500px; background:radial-gradient(circle,rgba(25,135,84,0.05) 0%,transparent 70%); border-radius:50% }
  .hero-content { text-align:center; position:relative; z-index:1; max-width:900px; margin:0 auto }
  .hero-badge { display:inline-flex; align-items:center; gap:10px; background:white; padding:12px 24px; border-radius:50px; box-shadow:0 4px 20px rgba(0,0,0,0.06); margin-bottom:32px; font-size:14px; font-weight:600; color:#198754; border:1px solid #d1f4e0; animation:fadeInUp 0.6s ease-out }
  .badge-icon { font-size:16px }
  .hero-title { font-size:56px; font-weight:900; line-height:1.2; color:#0f172a; margin-bottom:24px; letter-spacing:-1px; animation:fadeInUp 0.6s ease-out 0.2s backwards }
  .gradient-text { background:linear-gradient(135deg,#fd7e14 0%,#dc3545 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text }

  /* Loading & Error */
  .loading-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:80px 32px; color:#9ca3af }
  .spin-icon { font-size:40px; margin-bottom:16px; animation:spin 1s linear infinite; color:#198754 }
  .loading-state p { font-size:16px; font-weight:500 }
  .error-state { text-align:center; padding:80px 32px; background:#f8fafc; border-radius:20px; border:1px solid #e2e8f0 }
  .error-icon { font-size:56px; color:#fd7e14; margin-bottom:20px }
  .error-state h3 { font-size:24px; font-weight:700; color:#334155; margin-bottom:10px }
  .error-state p { font-size:15px; color:#64748b; margin-bottom:28px }

  /* Image placeholder */
  .cert-image-placeholder { width:100%; height:100%; background:linear-gradient(135deg,#198754,#20c997); display:flex; align-items:center; justify-content:center; font-size:56px; color:rgba(255,255,255,0.3) }

  /* Stats */
  .stats-section { padding:60px 0; background:white }
  .stats-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:24px }
  .stat-card { display:flex; align-items:center; gap:20px; background:white; border-radius:16px; padding:28px 24px; box-shadow:0 2px 12px rgba(0,0,0,0.06); border:1px solid #f1f5f9; transition:all 0.3s ease }
  .stat-card:hover { transform:translateY(-4px); box-shadow:0 8px 24px rgba(0,0,0,0.1); border-color:var(--stat-color) }
  .stat-icon { width:60px; height:60px; border-radius:12px; background:var(--stat-color); display:flex; align-items:center; justify-content:center; color:white; font-size:24px; flex-shrink:0 }
  .stat-value { font-size:32px; font-weight:900; color:#0f172a; line-height:1; margin-bottom:4px }
  .stat-label { font-size:14px; color:#64748b; font-weight:600 }

  /* Section headers */
  .section-header { text-align:left; margin-bottom:60px }
  .section-header.centered { text-align:center }
  .section-badge { display:inline-flex; align-items:center; gap:10px; background:#f1f5f9; padding:8px 20px; border-radius:50px; margin-bottom:20px; font-size:14px; font-weight:600; color:#475569 }
  .badge-dot { width:8px; height:8px; border-radius:50%; background:linear-gradient(135deg,#198754,#20c997); animation:pulse 2s ease-in-out infinite }
  .section-title { font-size:42px; font-weight:900; color:#0f172a; margin-bottom:16px; line-height:1.2 }
  .highlight-text { background:linear-gradient(135deg,#fd7e14 0%,#dc3545 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text }
  .section-description { font-size:17px; color:#64748b; max-width:700px; margin:0 auto; line-height:1.7 }

  /* Certifications list */
  .certifications-list-section { padding:100px 0; background:white; margin-top:-100px }
  .filters-section { display:flex; flex-wrap:wrap; gap:20px; align-items:center; margin-bottom:48px; padding:24px; background:#f8fafc; border-radius:16px; border:1px solid #e2e8f0 }
  .search-box { flex:1; min-width:300px; position:relative }
  .search-icon { position:absolute; left:16px; top:50%; transform:translateY(-50%); color:#94a3b8; font-size:18px }
  .search-input { width:100%; padding:14px 16px 14px 48px; border:1px solid #e2e8f0; border-radius:12px; font-size:15px; font-family:inherit; background:white; transition:all 0.3s ease }
  .search-input:focus { outline:none; border-color:#198754; box-shadow:0 0 0 4px rgba(25,135,84,0.1) }
  .filters-group { display:flex; gap:16px; flex-wrap:wrap }
  .filter-item { display:flex; align-items:center; gap:10px; padding:10px 16px; background:white; border:1px solid #e2e8f0; border-radius:12px; transition:all 0.3s ease }
  .filter-item:focus-within { border-color:#198754 }
  .filter-icon { color:#198754; font-size:16px }
  .filter-select { border:none; background:transparent; font-size:14px; font-weight:600; color:#334155; cursor:pointer; font-family:inherit; outline:none; min-width:180px }
  .results-count { display:flex; align-items:center; gap:8px; font-size:14px; font-weight:700; color:#0f172a; background:white; padding:10px 20px; border-radius:10px; border:1px solid #e2e8f0 }
  .results-count svg { color:#20c997 }

  /* Certif grid */
  .certifications-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(380px,1fr)); gap:32px }
  .certification-card { background:white; border-radius:20px; overflow:hidden; box-shadow:0 2px 12px rgba(0,0,0,0.06); transition:all 0.4s ease; border:1px solid #f1f5f9; animation:fadeInUp 0.6s ease-out backwards }
  .certification-card:hover { transform:translateY(-8px); box-shadow:0 12px 40px rgba(0,0,0,0.15); border-color:var(--cert-color) }
  .cert-header { position:relative; height:200px; overflow:hidden }
  .cert-image-wrapper { width:100%; height:100% }
  .cert-image { width:100%; height:100%; object-fit:cover; transition:transform 0.6s ease }
  .certification-card:hover .cert-image { transform:scale(1.1) }
  .cert-overlay { position:absolute; top:0; left:0; right:0; bottom:0; background:linear-gradient(180deg,transparent 0%,rgba(0,0,0,0.5) 100%) }
  .cert-badge { position:absolute; top:16px; right:16px; display:flex; align-items:center; gap:8px; padding:8px 16px; border-radius:8px; background:rgba(255,255,255,0.95); color:#198754; font-size:13px; font-weight:700; box-shadow:0 4px 12px rgba(0,0,0,0.15); z-index:2 }
  .cert-code-badge { position:absolute; top:16px; left:16px; padding:8px 16px; border-radius:8px; background:rgba(25,135,84,0.95); color:white; font-size:12px; font-weight:700; letter-spacing:0.5px; box-shadow:0 4px 12px rgba(0,0,0,0.15); z-index:2 }
  .cert-content { padding:28px }
  .cert-meta { display:flex; align-items:center; gap:16px; margin-bottom:16px; flex-wrap:wrap }
  .cert-domain,.cert-duration { display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:700 }
  .cert-domain svg,.cert-duration svg { font-size:12px }
  .cert-duration { color:#64748b }
  .cert-title { font-size:20px; font-weight:800; color:#0f172a; margin-bottom:12px; line-height:1.3 }
  .cert-description { font-size:15px; color:#64748b; line-height:1.6; margin-bottom:24px }
  .cert-details { display:flex; flex-direction:column; gap:20px; margin-bottom:24px }
  .detail-section h4 { display:flex; align-items:center; gap:8px; font-size:14px; font-weight:700; color:#0f172a; margin-bottom:12px }
  .detail-section h4 svg { color:#198754; font-size:14px }
  .detail-section ul { list-style:none; display:flex; flex-direction:column; gap:8px }
  .detail-section li { display:flex; align-items:flex-start; gap:8px; font-size:14px; color:#475569 }
  .detail-section li svg { color:#20c997; font-size:12px; margin-top:3px; flex-shrink:0 }
  .more-items { font-size:13px; color:#64748b; font-style:italic; padding-left:20px }
  .debouches-tags { display:flex; flex-wrap:wrap; gap:8px }
  .tag { display:inline-block; padding:6px 12px; background:#f1f5f9; color:#334155; border-radius:6px; font-size:12px; font-weight:600 }
  .cert-footer { display:flex; justify-content:space-between; align-items:center; padding:20px 0; border-top:1px solid #f1f5f9; margin-bottom:20px }
  .cert-info-row { display:flex; gap:16px }
  .info-item { display:flex; align-items:center; gap:6px; font-size:13px; color:#64748b; font-weight:600 }
  .info-item svg { color:#198754; font-size:14px }
  .cert-price { display:flex; flex-direction:column; align-items:flex-end }
  .price-label { font-size:12px; color:#94a3b8; font-weight:600; margin-bottom:2px }
  .price-value { font-size:18px; font-weight:900; color:#198754 }
  .btn-inscription { width:100%; display:flex; align-items:center; justify-content:center; gap:12px; padding:16px 24px; background:linear-gradient(135deg,#dc3545 0%,#fd7e14 100%); color:white; border:none; border-radius:12px; font-size:15px; font-weight:700; cursor:pointer; transition:all 0.3s ease; font-family:inherit; box-shadow:0 4px 20px rgba(220,53,69,0.3) }
  .btn-inscription:hover { transform:translateY(-3px); box-shadow:0 8px 30px rgba(220,53,69,0.4) }
  .other-certifications-section { margin-top:80px; text-align:center }
  .divider-line { height:2px; background:linear-gradient(90deg,transparent 0%,#e2e8f0 50%,transparent 100%); margin-bottom:40px }
  .btn-other-certifications { display:inline-flex; align-items:center; gap:12px; padding:18px 40px; background:linear-gradient(135deg,#198754 0%,#20c997 100%); color:white; border:none; border-radius:14px; font-size:17px; font-weight:700; cursor:pointer; transition:all 0.3s ease; font-family:inherit; text-decoration:none; box-shadow:0 4px 20px rgba(25,135,84,0.3) }
  .btn-other-certifications:hover { transform:translateY(-4px); box-shadow:0 8px 30px rgba(25,135,84,0.4) }

  /* No results */
  .no-results { text-align:center; padding:80px 32px; background:#f8fafc; border-radius:20px; border:1px solid #e2e8f0 }
  .no-results-icon { font-size:64px; color:#cbd5e1; margin-bottom:24px }
  .no-results h3 { font-size:24px; font-weight:700; color:#334155; margin-bottom:12px }
  .no-results p { font-size:16px; color:#64748b; margin-bottom:32px }
  .btn-reset { padding:14px 32px; background:linear-gradient(135deg,#198754 0%,#20c997 100%); color:white; border:none; border-radius:12px; font-size:16px; font-weight:600; cursor:pointer; transition:all 0.3s ease; font-family:inherit }
  .btn-reset:hover { transform:translateY(-3px); box-shadow:0 8px 24px rgba(25,135,84,0.3) }

  /* Advantages */
  .advantages-section { padding:100px 0; background:#f8fafc }
  .advantages-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:32px }
  .advantage-card { background:white; border-radius:20px; padding:40px 32px; box-shadow:0 2px 12px rgba(0,0,0,0.06); transition:all 0.4s ease; border:1px solid #f1f5f9 }
  .advantage-card:hover { transform:translateY(-8px); box-shadow:0 12px 40px rgba(0,0,0,0.12) }
  .advantage-icon { width:70px; height:70px; margin-bottom:24px; border-radius:16px; display:flex; align-items:center; justify-content:center; color:white; font-size:32px; transition:all 0.3s ease }
  .advantage-card:hover .advantage-icon { transform:scale(1.1) rotate(-5deg) }
  .advantage-card h3 { font-size:20px; font-weight:700; color:#0f172a; margin-bottom:12px }
  .advantage-card p { font-size:15px; color:#64748b; line-height:1.7 }

  /* Process */
  .process-section { padding:100px 0; background:#f8fafc }
  .process-timeline { display:grid; grid-template-columns:repeat(5,1fr); gap:0; position:relative }
  .process-step { display:flex; flex-direction:column; align-items:center; position:relative }
  .step-number { width:50px; height:50px; background:linear-gradient(135deg,#198754 0%,#20c997 100%); border-radius:50%; display:flex; align-items:center; justify-content:center; color:white; font-size:18px; font-weight:900; box-shadow:0 4px 12px rgba(25,135,84,0.3); margin-bottom:24px; z-index:2; position:relative }
  .step-content { text-align:center; background:white; padding:32px 20px; border-radius:16px; box-shadow:0 2px 12px rgba(0,0,0,0.06); transition:all 0.4s ease; border:1px solid #f1f5f9 }
  .process-step:hover .step-content { transform:translateY(-8px); box-shadow:0 12px 40px rgba(0,0,0,0.12) }
  .step-icon { width:60px; height:60px; margin:0 auto 20px; background:linear-gradient(135deg,#d1f4e0 0%,#b8f5d8 100%); border-radius:12px; display:flex; align-items:center; justify-content:center; color:#198754; font-size:28px; transition:all 0.3s ease }
  .process-step:hover .step-icon { transform:scale(1.1) rotate(-5deg) }
  .step-content h3 { font-size:18px; font-weight:700; color:#0f172a; margin-bottom:12px }
  .step-content p { font-size:14px; color:#64748b; line-height:1.6 }
  .step-connector { position:absolute; top:25px; left:50%; width:100%; height:2px; background:#cbd5e1; z-index:1 }

  /* Responsive */
  @media(max-width:1024px){ .hero-title{font-size:44px} .stats-grid{grid-template-columns:repeat(2,1fr)} .advantages-grid{grid-template-columns:1fr} .certifications-grid{grid-template-columns:repeat(auto-fill,minmax(320px,1fr))} .process-timeline{grid-template-columns:1fr;gap:32px} .step-connector{display:none} }
  @media(max-width:768px){ .cinef-hero{padding:80px 0 60px} .hero-title{font-size:36px} .stats-grid{grid-template-columns:1fr} .stat-card{flex-direction:column;text-align:center} .filters-section{flex-direction:column;align-items:stretch} .search-box,.filter-item{width:100%;min-width:100%} .filter-select{width:100%} .certifications-grid{grid-template-columns:1fr} .cert-footer{flex-direction:column;gap:16px;align-items:stretch} .cert-price{align-items:flex-start} }
  @media(max-width:640px){ .cinef-container{padding:0 16px} .hero-title{font-size:28px} .section-title{font-size:28px} .cert-content{padding:20px} .cert-title{font-size:18px} }
`;

export default CertificationsCINEF;